import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { showToast, showFailToast } from 'vant'
import store from '@/store'
import i18n from '@/i18n' // 导入 i18n 实例

import { getUserLang } from '@/storage/user'

// const { t } = useI18n()

// 定义响应数据类型
export interface ResponseData<T = any> {
  code: string
  data: T
  message: string
  success: boolean
}

// 扩展AxiosRequestConfig以支持自定义配置
declare module 'axios' {
  interface AxiosRequestConfig {
    showLoading?: boolean
    metadata?: {
      startTime: number
    }
  }
}

// 创建axios实例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    // baseURL: '/authenti-guard', // import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      // 显示加载提示
      if (config.showLoading !== false) {
        // const lang = getUserLang()
        // const loadingMessage = lang === 'zh-CN' ? '加载中...' : 'loading...'
        showToast({
          message: 'loading...',
          forbidClick: false,
          loadingType: 'spinner'
        })
      }

      // 添加认证token
      const token = store.state.user.token || localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 添加语言头
      const lang = getUserLang()
      // config.headers['Accept-Language'] = lang === 'zh-CN' ? 'zh' : 'en'
      config.headers['Accept-Language'] = lang

      // 记录请求开始时间（用于计算请求耗时）
      config.metadata = { startTime: new Date().getTime() }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ResponseData>) => {
      // 隐藏加载提示
      if (response.config.showLoading !== false) {
        // showToast.clear()
      }

      // 计算请求耗时
      const endTime = new Date().getTime()
      const startTime = response.config.metadata?.startTime
      if (startTime) {
        const duration = endTime - startTime
        // console.log(`请求 ${response.config.url} 耗时: ${duration}ms`)
      }

      // 处理响应数据
      const { data } = response
      // console.log('request response', response, data)

      // 根据业务代码处理不同情况
      if (data.code === '000000') {
        return data.data
      } else if (data.code === '010001') {
        // 未授权，清除token并跳转到登录页
        store.commit('user/LOGOUT')
        localStorage.removeItem('auth_token')
        // window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname)
        return Promise.reject(new Error('登录已过期，请重新登录'))
      } else {
        // 其他错误情况
        // showFailToast(data.message || '请求失败')
        return Promise.reject(new Error(data.message || '请求失败'))
      }
    },
    (error) => {
      // 隐藏加载提示
      // showToast.clear()

      // 处理HTTP错误状态码
      let message = '网络错误，请稍后重试'

      if (error.response) {
        const { status, data } = error.response

        switch (status) {
          case 400:
            message = data.message || '请求参数错误'
            break
          case 401:
            message = '登录已过期，请重新登录'
            // 清除token并跳转到登录页
            store.commit('user/LOGOUT')
            localStorage.removeItem('auth_token')
            window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname)
            break
          case 403:
            message = '没有权限访问该资源'
            break
          case 404:
            message = '请求的资源不存在'
            break
          case 500:
            message = '服务器内部错误'
            break
          case 502:
            message = '网关错误'
            break
          case 503:
            message = '服务不可用'
            break
          default:
            message = `连接错误${status}`
        }
      } else if (error.request) {
        message = '网络连接异常，请检查网络设置'
      } else {
        message = error.message
      }

      // showFailToast(message)
      return Promise.reject(error)
    }
  )

  return instance
}

// 创建默认实例
const request = createAxiosInstance()

// 封装常用请求方法
export const get = <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.get(url, { params, ...config })
}

export const post = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.post(url, data, config)
}

export const put = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.put(url, data, config)
}

export const del = <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> => {
  return request.delete(url, { params, ...config })
}

export const upload = <T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> => {
  return request.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    ...config
  })
}

export default request
